import { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  Check, 
  Trash2, 
  Eye,
  EyeOff,
  RefreshCw,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { flaggedQueriesApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const categoryColors = {
  'harassment': 'bg-orange-100 text-orange-800',
  'harassment/threatening': 'bg-red-100 text-red-800',
  'hate': 'bg-red-100 text-red-800',
  'hate/threatening': 'bg-red-200 text-red-900',
  'self-harm': 'bg-purple-100 text-purple-800',
  'self-harm/intent': 'bg-purple-200 text-purple-900',
  'self-harm/instructions': 'bg-purple-200 text-purple-900',
  'sexual': 'bg-pink-100 text-pink-800',
  'sexual/minors': 'bg-red-300 text-red-900',
  'violence': 'bg-orange-100 text-orange-800',
  'violence/graphic': 'bg-red-100 text-red-800',
  'llm_policy_violation': 'bg-yellow-100 text-yellow-800'
};

export const FlaggedQueriesManager = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'reviewed', 'all'
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const { toast } = useToast();

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const reviewed = filter === 'pending' ? false : filter === 'reviewed' ? true : null;
      const data = await flaggedQueriesApi.getAll(reviewed);
      setQueries(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load flagged queries',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, [filter]);

  const handleReview = async () => {
    if (!selectedQuery) return;
    
    try {
      await flaggedQueriesApi.review(selectedQuery.id, adminNotes || null);
      toast({
        title: 'Query Reviewed',
        description: 'The flagged query has been marked as reviewed.'
      });
      setShowReviewDialog(false);
      setSelectedQuery(null);
      setAdminNotes('');
      fetchQueries();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to review query',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (queryId) => {
    if (!confirm('Are you sure you want to delete this flagged query?')) return;
    
    try {
      await flaggedQueriesApi.delete(queryId);
      toast({
        title: 'Query Deleted',
        description: 'The flagged query has been deleted.'
      });
      fetchQueries();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete query',
        variant: 'destructive'
      });
    }
  };

  const openReviewDialog = (query) => {
    setSelectedQuery(query);
    setAdminNotes(query.admin_notes || '');
    setShowReviewDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = queries.filter(q => !q.reviewed).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Content Moderation</h2>
            <p className="text-sm text-muted-foreground">
              Review flagged AI search queries
            </p>
          </div>
        </div>
        
        <Button variant="outline" size="sm" onClick={fetchQueries} disabled={loading}>
          <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('pending')}
          className="gap-2"
        >
          <EyeOff className="w-4 h-4" />
          Pending Review
          {pendingCount > 0 && (
            <Badge variant="destructive" className="ml-1">
              {pendingCount}
            </Badge>
          )}
        </Button>
        <Button
          variant={filter === 'reviewed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('reviewed')}
        >
          <Eye className="w-4 h-4 mr-2" />
          Reviewed
        </Button>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All
        </Button>
      </div>

      {/* Queries List */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : queries.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {filter === 'pending' 
                ? 'No pending flagged queries'
                : filter === 'reviewed'
                ? 'No reviewed queries'
                : 'No flagged queries found'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {queries.map(query => (
            <Card 
              key={query.id}
              className={cn(
                "transition-all",
                query.reviewed ? "opacity-70" : "border-orange-200 bg-orange-50/30"
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Query Text */}
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
                      <p className="text-sm font-medium text-foreground break-words">
                        "{query.query}"
                      </p>
                    </div>
                    
                    {/* Flagged Categories */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {query.flagged_categories?.map(cat => (
                        <Badge 
                          key={cat} 
                          variant="secondary"
                          className={cn("text-xs", categoryColors[cat] || "bg-gray-100")}
                        >
                          {cat}
                        </Badge>
                      ))}
                    </div>
                    
                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{formatDate(query.created_at)}</span>
                      {query.ip_address && <span>IP: {query.ip_address}</span>}
                      {query.reviewed && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <Check className="w-3 h-3 mr-1" />
                          Reviewed
                        </Badge>
                      )}
                    </div>
                    
                    {/* Admin Notes */}
                    {query.admin_notes && (
                      <div className="mt-2 p-2 bg-muted rounded text-xs">
                        <MessageSquare className="w-3 h-3 inline mr-1" />
                        {query.admin_notes}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 flex-shrink-0">
                    {!query.reviewed && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openReviewDialog(query)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(query.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Flagged Query</DialogTitle>
            <DialogDescription>
              Mark this query as reviewed and optionally add notes.
            </DialogDescription>
          </DialogHeader>
          
          {selectedQuery && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">"{selectedQuery.query}"</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedQuery.flagged_categories?.map(cat => (
                    <Badge 
                      key={cat} 
                      variant="secondary"
                      className={cn("text-xs", categoryColors[cat])}
                    >
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Admin Notes (optional)
                </label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any notes about this query..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleReview}>
              <Check className="w-4 h-4 mr-2" />
              Mark as Reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
